import React, {useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {LoggedInView} from "../common/LoggedInView";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {getCurrentInquiry} from "../../store/reducers/InquiriesSlice";
import OrderComponent from "../../models/OrderComponent";
import Breadcrumbs from "../common/Breadcrumbs";
import {Field, FieldProps, Form, Formik, FormikProvider, useFormik} from "formik";
import Loadings, {DEFAULT_CONTACT} from "./components/Loadings";
import {getCountries} from "../../store/reducers/CountriesSlice";
import {getServices, resetUpdateServiceStatus} from "../../store/reducers/ServicesSlice";
import {getUsers} from "../../store/reducers/UsersSlice";
import {clearCurrentOrder, createOrder, getCurrentOrder, resetUpdateOrderStatus, updateOrder} from "../../store/reducers/OrdersSlice";
import {RequestStatus} from "../../utils/Enums";
import {apiClient} from "../../utils/ApiClient";
import ElogUpload from "../common/form/ElogUpload";
import Loading, {LoadingType} from "../../models/Loading";
import OrderForm from "./components/OrderForm";
import { Option } from "../common/form/ElogSelect";
import OrderFile from "../../models/OrderFile";
import {updateOfflineReason} from "../../store/reducers/OfflineReasonsSlice";
import {getToday} from "../../store/reducers/InfoSlice";
import {clearRecoveredFormAfterSuccessfulSave} from "../../utils/RecoveredFormStorage";

interface ModifyOrderScreenProps {
}

export interface NewOrderFormProps {
    user: number;
    expires_at: string;
}

const AUTOSAVE_FLAG = '__autosaved';

export const ModifyOrderScreen: React.FC<ModifyOrderScreenProps> = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const STORAGE_KEY = id ? `modifyOrderFormData:${id}` : '';
    const savedValues: any = useMemo(() => {
        if (!STORAGE_KEY) return {};
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (!saved) return {};
        try {
            const parsed = JSON.parse(saved);
            if (parsed?.[AUTOSAVE_FLAG] !== true) return {};
            const {[AUTOSAVE_FLAG]: _autosaved, ...values} = parsed;
            return Object.values(values).some(v => v !== "" && v !== false && v !== null) ? values : {};
        } catch {
            return {};
        }
    }, [STORAGE_KEY]);

    const resetStorage = () => {
        if (STORAGE_KEY) sessionStorage.removeItem(STORAGE_KEY);
    };

    const ordersState = useAppSelector((state) => state.orders);
    const servicesState = useAppSelector((state) => state.services);
    const countriesState = useAppSelector(state => state.countries);
    const infoState = useAppSelector(state => state.info);

    const requestStatus: RequestStatus = ordersState.updateOrderStatus;

    const [orderComponents, setOrderComponents] = useState<OrderComponent[]>([]);
    const [selectedService, setSelectedService] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({});
    const [loadedOrderId, setLoadedOrderId] = useState<string | null>(null);
    const [toOrderSubmit, setToOrderSubmit] = useState(false);

    const [clientContactsOptions, setClientContactsOptions] = useState<Option[]>([]);

    const breadcrumbLinks: BreadcrumbLink[] = useMemo(() => [
        { text: "Veod", url: "/orders" },
        { text: "Veo muutmine", url: `/orders/${id}/modify` },
    ], [id]);

    const handleCancel = () => {
        resetStorage();
        navigate(-1);
    };

    useEffect(() => {
        dispatch(getServices(true));
        dispatch(getCountries());
        dispatch(getUsers());
        setInitialValues({});
        setLoadedOrderId(null);
        setSelectedService(null);
        setClientContactsOptions([]);

        if (!id) {
            dispatch(clearCurrentOrder());
            return;
        }

        let active = true;
        dispatch(getCurrentOrder({ id }))
            .unwrap()
            .then((order) => {
                if (active && order?.id === parseInt(id)) {
                    setLoadedOrderId(String(order.id));
                }
            })
            .catch(() => {});

        return () => {
            active = false;
            dispatch(clearCurrentOrder());
        };
    }, [id, dispatch]);

    useEffect(() => {
        if (loadedOrderId !== id) return;
        const storedService = savedValues.service;
        const orderService = ordersState?.currentOrder?.service_id;
        const storedStr = String(storedService ?? '');
        const orderStr = String(orderService ?? '');
        if ((!storedStr || storedStr === '') && (!orderStr || orderStr === '')) return;
        if (storedStr && storedStr !== '') {
            setSelectedService(storedStr);
        } else if (orderStr && orderStr !== '') {
            setSelectedService(orderStr);
        }
    }, [ordersState?.currentOrder?.service_id, savedValues.service, loadedOrderId, id]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await apiClient.get(`services/${selectedService}`);
                const data = response.data;
                setOrderComponents(data?.order_components_hierarchical || []);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        if (selectedService !== null) {
            fetchData();
        }
    }, [selectedService]);

    useEffect(() => {
        dispatch(getToday());
        dispatch(resetUpdateOrderStatus());
    }, [location]);

    const hasLoadings = useMemo(() => (
        orderComponents.findIndex(component => component.component === "Loadings") > -1
    ), [orderComponents]);

    const countryOptions = useMemo(() => (
        countriesState.countries.map((country) => ({
            value: country.id.toString(),
            label: country.name,
        }))
    ), [countriesState.countries]);

    const clientContacts = useMemo(() => (
        (ordersState?.currentOrder?.client_contacts ?? []).map(contact => ({
            value: contact.id.toString(),
            label: contact.name ?? ""
        }))
    ), [ordersState?.currentOrder?.client_contacts]);

    useEffect(() => {
        const fromOrder = (ordersState?.currentOrder?.client_contacts ?? []).map(contact => ({
            value: contact.id.toString(),
            label: contact.name ?? ""
        }));
        if (fromOrder.length > 0) {
            setClientContactsOptions(fromOrder);
            return;
        }
        const clientId = ordersState?.currentOrder?.client_id;
        if (!clientId) return;
        const load = async () => {
            try {
                const res = await apiClient.get(`clients/${clientId}/contacts`);
                const list = (res.data ?? []).map((c: any) => ({ value: c.id.toString(), label: c.name ?? "" }));
                setClientContactsOptions(list);
            } catch (e) {
            }
        };
        load();
    }, [ordersState?.currentOrder?.client_contacts, ordersState?.currentOrder?.client_id]);

    const vendorContacts = useMemo(() => (
        (ordersState?.currentOrder?.vendor_contacts ?? []).map(contact => ({
            value: contact.id.toString(),
            label: contact.name ?? ""
        }))
    ), [ordersState?.currentOrder?.vendor_contacts]);

    useEffect(() => {
        if (loadedOrderId !== id || !ordersState.currentOrder || !id || ordersState.currentOrder.id !== parseInt(id) || Object.keys(initialValues).length > 0) return;
        setInitialValues({
            checker: savedValues.checker ?? ordersState.currentOrder.checker_id,
            client: savedValues.client ?? (ordersState.currentOrder.client?.name ?? ''),
            vendor: savedValues.vendor ?? ordersState.currentOrder.vendor?.name,
            service: savedValues.service ?? String(ordersState.currentOrder.service_id ?? ''),
            amount: savedValues.amount ?? ordersState.currentOrder.amount,
            meters: savedValues.meters ?? ordersState.currentOrder.meters,
            weight: savedValues.weight ?? ordersState.currentOrder.weight,
            buySum: savedValues.buySum ?? ordersState.currentOrder.buy_sum,
            buyCurrency: savedValues.buyCurrency ?? (ordersState.currentOrder.buy_currency || 'EUR'),
            buySumOriginal: savedValues.buySumOriginal ?? ordersState.currentOrder.buy_sum_original,
            buyExchangeRate: ordersState.currentOrder.buy_exchange_rate,
            buyExchangeRateDate: ordersState.currentOrder.buy_exchange_rate_date,
            saleSum: savedValues.saleSum ?? ordersState.currentOrder.sale_sum,
            orderTime: savedValues.orderTime ?? ordersState.currentOrder.order_time,
            loadingTime: savedValues.loadingTime ?? ordersState.currentOrder.loading_time,
            buyInvoice: savedValues.buyInvoice ?? ordersState.currentOrder.buy_invoice,
            saleInvoice: savedValues.saleInvoice ?? ordersState.currentOrder.sale_invoice,
            buyPaid: savedValues.buyPaid ?? (ordersState.currentOrder.buy_paid === 1),
            salePaid: savedValues.salePaid ?? (ordersState.currentOrder.sale_paid === 1),
            website: savedValues.website ?? (ordersState.currentOrder.website === 1),
            cmr: savedValues.cmr ?? (ordersState.currentOrder.cmr === 1),
            reclamation: savedValues.reclamation ?? (ordersState.currentOrder.reclamation === 1),
            refNumber: savedValues.refNumber ?? ordersState.currentOrder.ref_number,
            containerNumber: savedValues.containerNumber ?? ordersState.currentOrder.container_number ?? '',
            awbNumber: savedValues.awbNumber ?? ordersState.currentOrder.awb_number ?? '',
            carNumber: savedValues.carNumber ?? ordersState.currentOrder.car_number,
            terminal: savedValues.terminal ?? ordersState.currentOrder.terminal,
            loadingInstructions: savedValues.loadingInstructions ?? ordersState.currentOrder.loading_instructions,
            unloadingInstructions: savedValues.unloadingInstructions ?? ordersState.currentOrder.unloading_instructions,
            comment: savedValues.comment ?? ordersState.currentOrder.comment,
            loadings: savedValues.loadings?.length > 0 ? savedValues.loadings : ordersState.currentOrder.loadings?.map(l => ({ ...l, country: l.country_id })),
            unloadings: savedValues.unloadings?.length > 0 ? savedValues.unloadings : ordersState.currentOrder.unloadings?.map(u => ({ ...u, country: u.country_id })),
            clientContact: savedValues.clientContact ?? (ordersState.currentOrder.client_contacts ?? []).map(c => String(c.id)),
            vendorContact: savedValues.vendorContact ?? (ordersState.currentOrder.vendor_contacts ?? []).map(c => String(c.id)),
            loaded: savedValues.loaded ?? (ordersState.currentOrder.loaded === 1),
            unloaded: savedValues.unloaded ?? (ordersState.currentOrder.unloaded === 1),
            documentsSent: savedValues.unloaded ?? (ordersState.currentOrder.documents_sent === 1),
        });
    }, [ordersState.currentOrder, savedValues, initialValues, id, loadedOrderId]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success && toOrderSubmit) {
            resetStorage();
            navigate('/orders');
            setToOrderSubmit(false);
        }
    }, [requestStatus, toOrderSubmit, navigate]);

    const formik = useFormik({
        initialValues,
        onSubmit: (values: any) => {
            if (!id) return;
            const saveRequest = dispatch(updateOrder({ id, data: values })).unwrap();
            void clearRecoveredFormAfterSuccessfulSave(saveRequest, STORAGE_KEY);
        },
        enableReinitialize: true
    });

    const { values, setFieldValue, handleSubmit } = formik;

    useEffect(() => {
        const fetchServiceInfo = async (serviceId: string) => {
            try {
                const response = await apiClient.get(`services/${serviceId}`);
                const service = response.data;
                const insuranceCommission = infoState.settings?.comission;
                if (service?.is_insurance === 1 && insuranceCommission && values.saleSum != null) {
                    const commissionPercentage = parseFloat(insuranceCommission);
                    if (service?.id != 11) {
                        formik.setFieldValue('buySum', (values.saleSum * (1 - commissionPercentage / 100)).toFixed(2));
                    }
                }
            } catch (error) {
            }
        };
        if (values.service) fetchServiceInfo(values.service);
    }, [values.saleSum, values.service, infoState.settings]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            resetStorage();
            return;
        }
        if (loadedOrderId !== id || !ordersState.currentOrder || !id || ordersState.currentOrder.id !== parseInt(id)) return;
        if (!formik.initialValues.service || formik.initialValues.service === '') return;
        if (!Object.values(formik.initialValues).some(v => v !== "" && v !== false && v !== null)) return;
        if (!formik.dirty && Object.keys(savedValues).length === 0) {
            resetStorage();
            return;
        }
        const prevRaw = sessionStorage.getItem(STORAGE_KEY);
        const prev = prevRaw ? (() => { try { return JSON.parse(prevRaw); } catch { return {}; } })() : {};
        const next = JSON.parse(JSON.stringify({...values, [AUTOSAVE_FLAG]: true}));
        if (JSON.stringify(prev) !== JSON.stringify(next)) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        }
    }, [values, formik.dirty, ordersState.currentOrder, loadedOrderId, id, savedValues, requestStatus]);

    if (!ordersState?.currentOrder) {
        return null;
    }

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks} />
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Andmed muudetud</h2>
                        {(values.unloaded === true && values.checker === infoState?.me?.id) &&
                            <p>
                                <Link to={`/orders/${id}/rate`} style={{ color: 'red', fontSize: '20px' }}>
                                    Hinda vedaja kontakti
                                </Link>
                            </p>}
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && ordersState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {ordersState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <h1>Veo muutmine</h1>
                    {hasLoadings &&
                        <Loadings
                            countries={countryOptions}
                            orderComponents={orderComponents[orderComponents.findIndex(component => component.component === "Loadings")]?.children ?? []}
                            orderLoadings={values.loadings && values.loadings.length > 0 ? values.loadings : [{
                                country: ordersState?.currentOrder?.loadings?.[0]?.country_id,
                                contacts: [DEFAULT_CONTACT],
                                order_id: 0,
                                type: LoadingType.Loading,
                                city: ordersState?.currentOrder?.loadings?.[0]?.city,
                            }]}
                            orderUnloadings={values.unloadings && values.unloadings.length > 0 ? values.unloadings : [{
                                country: ordersState?.currentOrder?.unloadings?.[0]?.country_id,
                                contacts: [DEFAULT_CONTACT],
                                order_id: 0,
                                type: LoadingType.Unloading,
                                city: ordersState?.currentOrder?.unloadings?.[0]?.city,
                            }]}
                        />}
                    <table className="form">
                        <OrderForm
                            orderComponents={orderComponents}
                            onSelectedServiceChange={(service) => {
                                setSelectedService(service);
                            }}
                            clientContacts={clientContactsOptions}
                            vendorContacts={vendorContacts}
                            files={ordersState?.currentOrder?.files ?? []}
                            taxDept={ordersState?.currentOrder?.tax_dept == 1}
                            values={initialValues}
                            withDeletedServices={true}
                            isModify={true}
                        />
                    </table>
                    <div className="action clear">
                        <p>
                            <button type="button" style={{ marginRight: '1rem' }} onClick={() => { setFieldValue('draft', false); setToOrderSubmit(ordersState.currentOrder?.draft === 1); handleSubmit(); }}>
                                <span>
                                    <strong>{ordersState.currentOrder?.draft === 1 ? 'Veoks' : 'Muuda'}</strong>
                                </span>
                            </button>
                            {ordersState.currentOrder?.draft === 1 && (
                                <button type="button" style={{ backgroundColor: '#ccc', backgroundImage: 'none', color: 'black', marginRight: '1rem' }} onClick={() => {
                                    setFieldValue("draft", true);
                                    handleSubmit();
                                }}>
                                    <span>
                                        <strong>Muuda mustandit</strong>
                                    </span>
                                </button>
                            )}
                            <Link to="#" onClick={handleCancel}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Andmed muudetud</h2>
                        {(values.unloaded === true && values.checker === infoState?.me?.id) &&
                        <p>
                            <Link to={`/orders/${id}/rate`} style={{ color: 'red', fontSize: '20px' }}>
                                Hinda vedaja kontakti
                            </Link>
                        </p>}
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && ordersState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {ordersState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
        </LoggedInView>
    );
}
